import {
  Button,
  Alert,
  FormGroup,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Card,
} from "reactstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useToggle } from "@mantine/hooks";
import Cookies from "js-cookie";

import gcWordmark from "../../assets/img/growthcloud/growthcloud_wordmark.svg";
import linkedin from "../../assets/img/icons/common/linkedin.svg";
import AppHelmet from "../../components/AppComponents/AppHelmet";
import amazon from "../../assets/img/icons/common/amazon.svg";
import google from "../../assets/img/icons/common/google.svg";
import ms from "../../assets/img/icons/common/office365.svg";
import AuthFooter from "../../components/Footers/AuthFooter";
import Password from "../../components/Inputs/Password";
import AppSpinner from "../../elements/AppSpinner";
import OAuthButton from "./OAuthButton";
import API from "../../api/api/API";
import { Method } from "./Login";

type AppConfig = { Color: string; Logo: string; Name: string };

const AppConfig: Record<string, AppConfig> = {
  EmergeAI: {
    Color: "#00BFD5",
    Logo: "/img/emergeai.png",
    Name: "Emerge.ai",
  },
};

export const useAppConfig = (app: string | null) => {
  useEffect(() => {
    if (!app || !AppConfig[app]) return;

    document.documentElement.style.setProperty(
      "--tw-color-primary",
      AppConfig[app].Color
    );

    return () => {
      document.documentElement.style.setProperty(
        "--tw-color-primary",
        "#26B058"
      );
    };
  }, [app]);

  return AppConfig[app || ""] || null;
};

const Register = () => {
  const [search] = useSearchParams();
  const app = search.get("app");
  const appConfig = useAppConfig(app);

  return (
    <div
      style={{
        backgroundImage: `url(/img/login-bg.png)`,
      }}
      className="tw:flex tw:justify-center tw:items-center tw:bg-no-repeat tw:bg-cover tw:bg-fixed"
    >
      <AppHelmet title="Sign In or Get Started with GrowthCloud.com" />
      <div className="tw:flex tw:justify-center tw:w-full tw:overflow-auto">
        <div className="tw:md:flex tw:justify-center tw:items-center tw:h-screen tw:w-0 tw:min-[992px]:w-1/2">
          <p className="tw:text-white tw:text-6xl tw:font-bold tw:leading-relaxed tw:hidden tw:min-[992px]:block tw:max-w-[575px] tw:pl-8">
            AI-Powered Growth Solutions For Business
          </p>
        </div>
        <div className="tw:flex tw:justify-center tw:items-center tw:md:py-5 tw:md:pr-5 tw:w-full tw:min-[992px]:w-1/2">
          <RegisterCard appConfig={appConfig} />
        </div>
      </div>
    </div>
  );
};

const RegisterCard = ({ appConfig }: { appConfig?: AppConfig }) => {
  const navigate = useNavigate();
  const [error, setErrorState] = useState<any>({
    accountExistsError: null,
    localLoginError: null,
    invalidPassword: null,
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validPassword, setValidPassword] = useState(false);
  const [inviteMode, setInviteMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState<Method | false>(false);
  const [visibility, toggleVisibility] = useToggle();
  const [search] = useSearchParams();
  const [form, setForm] = useState<{
    name: string;
    email: string;
    password: string;
    passwordConfirm: string | null;
    method: "amazon" | "google" | "office365" | null;
    inviteToken: string | null;
  }>({
    name: "",
    email: "",
    password: "",
    passwordConfirm: null,
    method: null,
    inviteToken: null,
  });

  useEffect(() => {
    const url_string = window.location.href;
    const url = new URL(url_string);
    const inviteToken = url.searchParams.get("invite_token");
    const inviteEmail = url.searchParams.get("invite_email");

    if (inviteToken || inviteEmail) {
      setForm({ ...form, email: inviteEmail ?? "", inviteToken });
      setInviteMode(true);
    }
  }, []);

  return (
    <Card className="tw:max-[576px]:min-w-screen tw:max-[576px]:min-h-dvh tw:p-7 tw:pb-0 tw:flex tw:justify-between tw:m-0 tw:w-full tw:max-w-[500px]">
      <div>
        <a className="tw:pb-4" href="https://growthcloud.com">
          <img
            alt="..."
            src={appConfig?.Logo || gcWordmark}
            className="tw:w-1/2"
          />
        </a>
        <h2 className="tw:pt-5 tw:pb-2">Create an Account</h2>
        {appConfig ? (
          <p className="tw:pb-2 tw:font-bold tw:text-xs">
            Creating an account for{" "}
            <span className="tw:text-primary">{appConfig.Name}</span>
          </p>
        ) : (
          <p className="tw:pb-2 tw:font-bold tw:text-xs">
            Already have an account?{" "}
            <Link className="tw:text-primary tw:font-medium" to="/login">
              Login
            </Link>
          </p>
        )}
        <div className="tw:justify-center">
          <div className="tw:flex tw:justify-center tw:items-center tw:py-4">
            <OAuthButton
              onClick={() => redirectOAuth("office365")}
              logo={ms}
              redirecting={redirecting}
              method="office365"
            />
            <OAuthButton
              onClick={() => redirectOAuth("google")}
              logo={google}
              redirecting={redirecting}
              method="google"
            />
            <OAuthButton
              onClick={() => redirectOAuth("amazon")}
              logo={amazon}
              redirecting={redirecting}
              method="amazon"
            />
            <OAuthButton
              onClick={() => redirectOAuth("linkedin")}
              logo={linkedin}
              redirecting={redirecting}
              method="linkedin"
            />
          </div>
          {errorMessage ? (
            <Alert color="warning" className="tw:mt-4">
              {errorMessage}
            </Alert>
          ) : null}
          <div className="tw:py-4">
            <h5 className="tw:w-full tw:text-center tw:px-4 tw:border-b tw:border-solid tw:border-black tw:leading-[0.1rem]">
              <span className="tw:bg-white tw:px-3">or</span>
            </h5>
          </div>

          <div>
            <Form role="form" onSubmit={registerLocal}>
              <FormGroup>
                <h5 className="tw:text-dark">Name</h5>
                <Input
                  placeholder="Name"
                  type="text"
                  name="name"
                  autoComplete="name"
                  value={form?.name}
                  onChange={handleInputChange}
                  required={true}
                  className="tw:focus:border-primary"
                />
              </FormGroup>
              <FormGroup>
                <h5 className="tw:text-dark">Email</h5>
                <Input
                  placeholder="Email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={form?.email}
                  readOnly={inviteMode}
                  onChange={handleInputChange}
                  required={true}
                  className="tw:focus:border-primary"
                />
              </FormGroup>
              <FormGroup>
                <h5 className="tw:text-dark">Password</h5>
                <Password
                  onChange={handleInputChange}
                  // @ts-ignore
                  setValidPassword={(valid: any) => {
                    setValidPassword(valid);
                  }}
                  showIcon={false}
                  userEmail={form.email}
                />
                <InputGroup className="tw:mt-3">
                  <Input
                    type={visibility ? "text" : "password"}
                    name={"passwordConfirm"}
                    value={form?.passwordConfirm || ""}
                    onChange={handleInputChange}
                    autoComplete={"new-password"}
                    placeholder={"Confirm Password"}
                    required={true}
                    className="tw:border-r-0 tw:focus:border-gray-300"
                  />
                  <InputGroupAddon addonType="append">
                    <InputGroupText
                      className="tw:cursor-pointer"
                      onClick={() => toggleVisibility()}
                    >
                      <i
                        className={`tw:text-lg tw:leading-none ${
                          visibility ? "mdi mdi-eye-off" : "mdi mdi-eye"
                        }`}
                      />
                    </InputGroupText>
                  </InputGroupAddon>
                </InputGroup>
              </FormGroup>

              {form.passwordConfirm &&
                form.passwordConfirm !== form?.password && (
                  <Alert color="danger" className="tw:mb-4">
                    Passwords don't match!
                  </Alert>
                )}
              <Button
                disabled={
                  submitting ||
                  !validPassword ||
                  form?.passwordConfirm !== form?.password
                }
                className="tw:px-4 tw:w-full tw:bg-primary tw:border-primary"
                color="primary"
                type="submit"
              >
                {submitting ? (
                  <span className="tw:px-4">
                    <AppSpinner color="white" className="tw:h-5 tw:w-5" />
                  </span>
                ) : (
                  "Sign up"
                )}
              </Button>
              {error.accountExistsError && (
                <Alert color="warning" className="tw:mt-4">
                  An account with this email already exists.
                </Alert>
              )}
              {error.localLoginError && (
                <Alert color="warning" className="tw:mt-4">
                  {error.localLoginError}
                </Alert>
              )}
              {error.invalidPassword && (
                <Alert color="warning" className="tw:mt-4">
                  {error.invalidPassword}
                </Alert>
              )}
            </Form>
            <div className="tw:flex tw:justify-center tw:mt-6">
              <a
                className="tw:text-primary tw:text-[12px] tw:font-bold"
                href="https://www.emerge360.com/growthcloud"
                target="_blank"
                rel="norefferer"
              >
                Learn More About GrowthCloud
              </a>
            </div>
          </div>
        </div>
      </div>
      <AuthFooter />
    </Card>
  );

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    let { name, value } = event.target;
    if (name === "email") value = value.toLowerCase();

    setForm({ ...form, [name]: value });
  }

  function redirectOAuth(provider: Method) {
    const redirectTo = window.location.origin + "/oauth";
    const appRedirectTo = search.get("redirectTo");

    setRedirecting(provider);
    setTimeout(() => setRedirecting(false), 5000);

    const params = new URLSearchParams();

    params.set("app-id", search.get("app") || "GrowthCloud");
    params.set("redirectTo", redirectTo);
    if (form.inviteToken) params.set("inviteToken", form.inviteToken);
    if (appRedirectTo) {
      params.set("appRedirectTo", appRedirectTo);
    }

    window.location.href = `${
      window.GC_API_URL
    }/auth/providers/${provider}?${params.toString()}`;
  }

  function setError(
    name: "accountExistsError" | "localLoginError" | "invalidPassword",
    value: any
  ) {
    setErrorState({ ...error, [name]: value });

    if (value) {
      setTimeout(() => {
        setError(name, null);
      }, 6000);
    }
  }

  function registerLocal(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorMessage(null);
    setSubmitting(true);

    setError("localLoginError", null);

    API.registerLocal(
      form.name,
      form.email,
      form.password,
      inviteMode ? form?.inviteToken! : undefined,
      search.get("app") || undefined
    )
      .then(
        async (data) => {
          const registrationToken = data.data.data.registrationToken;

          Cookies.set("registrationToken", registrationToken, {
            sameSite: "strict",
            secure: window.location.origin.startsWith("https"),
          });

          // For signing up from Emerge.ai/other apps
          if (appConfig) {
            search.set("registrationToken", registrationToken);
          }

          let url = `/register/phone${
            !!search.toString() ? `?${search.toString()}` : ""
          }`;

          navigate(url);
        },
        (error) => {
          if (error.response && error.response.status === 409) {
            setError("accountExistsError", true);
          } else if (error.response.data.httpStatus === 400) {
            setError("invalidPassword", error.response.data.message);
          } else if (error.response.data.httpStatus === 401) {
            setError("localLoginError", error.response.data.message);
          } else {
            setError(
              "localLoginError",
              "We're having trouble creating your account. Please try again."
            );
          }
        }
      )
      .finally(() => setSubmitting(false));
  }
};

export default Register;
